import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Trash2 } from 'lucide-react';
import { useAuthenticatedApi } from '@/hooks/useAuthenticatedApi';
import { useQueryClient } from '@tanstack/react-query';
import { useFavorites } from '@/contexts/FavoritesContext';
import { showSuccessToast, showErrorToast, getErrorMessage } from '@/lib/toast-utils';

export interface EditListData {
  id: string;
  name: string;
  isDefault: boolean;
}

interface FavoriteListModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (list: { id: string; name: string }) => void;
  onDeleted?: () => void;
  hasExistingLists: boolean;
  totalLists: number;
  editList?: EditListData | null;
}

export function FavoriteListModal({ open, onOpenChange, onSaved, onDeleted, hasExistingLists, totalLists, editList = null }: FavoriteListModalProps) {
  const isEditing = !!editList;
  const forceDefault = !hasExistingLists || (isEditing && editList.isDefault && totalLists <= 1);

  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { makeAuthenticatedCall } = useAuthenticatedApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { clearFavoritesCache } = useFavorites();

  useEffect(() => {
    if (open && editList) {
      setName(editList.name);
      setIsDefault(editList.isDefault);
    } else if (open && !editList) {
      setName('');
      setIsDefault(!hasExistingLists);
    }
  }, [open, editList, hasExistingLists]);

  const reset = () => {
    setName('');
    setIsDefault(false);
    setIsSubmitting(false);
    setIsDeleting(false);
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setIsSubmitting(true);
    try {
      if (isEditing) {
        const result = await makeAuthenticatedCall<{ success: boolean; error?: string }>(
          'update-favorites-list',
          { listId: editList.id, name: name.trim(), isDefault: forceDefault || isDefault }
        );
        if (result.error || !result.data?.success) {
          showErrorToast(result.error || result.data?.error || 'Failed to update list', 'Error');
          return;
        }
        showSuccessToast(`"${name.trim()}" has been updated`, 'Favorite List Updated');
        await queryClient.invalidateQueries({ queryKey: ['get-favorites-lists'] });
        await queryClient.invalidateQueries({ queryKey: ['get-favorites-list-detail'] });
        reset();
        onOpenChange(false);
        onSaved({ id: editList.id, name: name.trim() });
      } else {
        const result = await makeAuthenticatedCall<{ success: boolean; id?: number; name?: string; error?: string }>(
          'create-favorites-list',
          { name: name.trim(), isDefault: forceDefault || isDefault }
        );
        if (result.error || !result.data?.success) {
          showErrorToast(result.error || result.data?.error || 'Failed to create list', 'Error');
          return;
        }
        showSuccessToast(`"${name.trim()}" has been created`, 'Favorite List Created');
        // Only invalidate list metadata — new list has 0 products, no need to refetch all items
        await queryClient.invalidateQueries({ queryKey: ['get-favorites-lists'] });
        const newId = String(result.data.id || '');
        reset();
        onOpenChange(false);
        onSaved({ id: newId, name: name.trim() });
      }
    } catch (err) {
      showErrorToast(getErrorMessage(err), 'Error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editList) return;
    const deletedName = editList.name;
    setIsDeleting(true);
    try {
      const result = await makeAuthenticatedCall<{ success: boolean; error?: string }>(
        'delete-favorites-list',
        { listId: editList.id }
      );
      if (result.error || !result.data?.success) {
        showErrorToast(result.error || result.data?.error || 'Failed to delete list', 'Error');
        return;
      }
      showSuccessToast(`"${deletedName}" has been deleted`, 'Favorite List Deleted');
      clearFavoritesCache();
      reset();
      onOpenChange(false);
      if (onDeleted) {
        onDeleted();
      } else {
        navigate('/my-favorites');
      }
    } catch (err) {
      showErrorToast(getErrorMessage(err), 'Error');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Favorite List' : 'New Favorite List'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="fav-list-name">Name</Label>
            <Input
              id="fav-list-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter list name"
              className="bg-card border-2 border-border shadow-sm"
              autoFocus
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="fav-list-default"
              checked={forceDefault || isDefault}
              disabled={forceDefault}
              onCheckedChange={(checked) => setIsDefault(checked === true)}
            />
            <Label htmlFor="fav-list-default" className={`text-sm ${forceDefault ? '' : 'cursor-pointer'}`}>
              Set as default list{forceDefault && isEditing ? ' (only list)' : ''}
            </Label>
          </div>
        </div>
        <DialogFooter className="flex-row justify-between sm:justify-between gap-2">
          {isEditing && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={isDeleting}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Favorite List</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete &quot;{editList?.name}&quot;? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={!name.trim() || isSubmitting}>
              {isSubmitting ? (isEditing ? 'Saving…' : 'Creating…') : (isEditing ? 'Save' : 'Create')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}